import React from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaTag, FaPercent } from 'react-icons/fa';

const shine = keyframes`
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
`;

const BannerContainer = styled(motion.div)`
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
  padding: 25px 30px;
  border-radius: 16px;
  margin: ${props => props.$margin || '40px 0'};
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 25px rgba(212, 175, 55, 0.3);
  border: 2px solid rgba(255, 255, 255, 0.2);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 50%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    animation: ${shine} 3s infinite;
  }
  
  @media (max-width: 768px) {
    padding: 20px;
    margin: ${props => props.$margin || '30px 0'};
  }
`;

const BannerContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    gap: 15px;
  }
`;

const BannerLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const IconWrapper = styled.div`
  width: 50px;
  height: 50px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  svg {
    color: white;
    font-size: 24px;
  }
  
  @media (max-width: 768px) {
    width: 45px;
    height: 45px;
    
    svg {
      font-size: 20px;
    }
  }
`;

const BannerText = styled.div`
  color: white;
`;

const BannerTitle = styled.h3`
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0 0 5px 0;
  color: white;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const BannerDescription = styled.p`
  font-size: 0.95rem;
  margin: 0;
  color: rgba(255, 255, 255, 0.95);
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const BannerButton = styled(Link)`
  background: white;
  color: var(--primary-color);
  padding: 12px 28px;
  border-radius: 50px;
  font-weight: 700;
  font-size: 1rem;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  white-space: nowrap;
  
  &:hover {
    transform: translateY(-2px) scale(1.05);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
    background: rgba(255, 255, 255, 0.95);
  }
  
  &:active {
    transform: translateY(0) scale(1);
  }
  
  @media (max-width: 768px) {
    padding: 10px 24px;
    font-size: 0.95rem;
  }
`;

const DiscountBadge = styled.span`
  background: rgba(255, 255, 255, 0.25);
  color: white;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 700;
  margin-left: 10px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  display: inline-flex;
  align-items: center;
  gap: 5px;
  
  svg {
    font-size: 0.85rem;
  }
  
  @media (max-width: 768px) {
    margin-left: 0;
    margin-top: 5px;
  }
`;

const PricingBanner = ({ 
  title = "Special Offer!", 
  description = "Get amazing discounts on all services",
  discount = "70% OFF",
  buttonText = "View Pricing",
  buttonLink = "/pricing",
  icon = "tag",
  margin
}) => {
  const IconComponent = icon === "percent" ? FaPercent : FaTag;
  
  return (
    <BannerContainer
      $margin={margin}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
    >
      <BannerContent>
        <BannerLeft>
          <IconWrapper>
            <IconComponent />
          </IconWrapper>
          <BannerText>
            <BannerTitle>
              {title}
              {discount && (
                <DiscountBadge>
                  <FaPercent /> {discount}
                </DiscountBadge>
              )}
            </BannerTitle>
            <BannerDescription>{description}</BannerDescription>
          </BannerText>
        </BannerLeft>
        <BannerButton to={buttonLink}>{buttonText}</BannerButton>
      </BannerContent>
    </BannerContainer>
  );
};

export default PricingBanner;
